import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import moulogo from "../../assets/mouLogo.png";
import CfoLanguageToggle from "../cfo/CfoLanguageToggle";
import { useEmpresasLanguageOptional } from "../../context/EmpresasLanguageContext";

const PRIMARY_SECTIONS = [
  {
    id: "personas",
    label: "Personas",
    description: "Productos, educación y trámites digitales.",
    path: "/personas",
    accent: "text-blue-700",
    links: [
      { label: "Inicio Personas", path: "/personas" },
      { label: "MisFICs", path: "/personas/misFICs" },
      { label: "MisCDTs", path: "/personas/simula-tu-cdt" },
      { label: "MiPensión", path: "/personas/miPension" },
      { label: "Trámites digitales", path: "/personas#tramites-digitales" },
      { label: "Educación financiera", path: "/blog/educacion-financiera" },
      { label: "Dashboard", path: "/personas/dashboard" },
    ],
    cta: {
      label: "Ingresar a Personas",
      path: "/personas/iniciar-sesion",
      color: "bg-blue-600 hover:bg-blue-700",
    },
  },
  {
    id: "empresas",
    label: "Empresas",
    description: "Soluciones de inversión y beneficios corporativos.",
    path: "/empresas",
    accent: "text-purple-700",
    links: [
      { label: "Inicio Empresas", path: "/empresas" },
      { label: "Beneficios", path: "/empresas#beneficios" },
      { label: "Dirección de innovación financiera", path: "/empresas/direccion-de-innovacion-financiera" },
      { label: "Gestión de caja", path: "/empresas/gestion-de-caja" },
      { label: "CEZ · Consejo Empresarial del Sumapaz", path: "/empresas/consejo-empresarial-sumapaz" },
      { label: "Gestión de aportes", path: "/empresas/gestion-de-aportes" },
      { label: "Sueldo Inteligente", path: "/sueldo-inteligente" },
      { label: "Blog empresarial", path: "/empresas/blog" },
      { label: "Trámites digitales", path: "/empresas/tramites-digitales" },
    ],
    cta: {
      label: "Ingresar a Empresas",
      path: "/empresas/iniciar-sesion",
      color: "bg-purple-600 hover:bg-purple-700",
    },
  },
  {
    id: "valor360",
    label: "Valor360",
    description: "Acompañamiento financiero personalizado.",
    path: "/valor360",
    accent: "text-[#102a72]",
    links: [
      { label: "Conoce Valor360", path: "/valor360" },
      { label: "Beneficios", path: "/valor360#beneficios" },
      { label: "¿En qué consiste?", path: "/valor360#en-que-consiste" },
      { label: "Acompañamiento", path: "/valor360#con-nosotros" },
      { label: "Qué esperar", path: "/valor360#esperar" },
      { label: "Agenda una sesión", path: "/vincula-tus-finanzas" },
    ],
    cta: {
      label: "Contactar asesor",
      path: "/vincula-tus-finanzas",
      color: "bg-[#1746a2] hover:bg-[#123070]",
    },
  },
  {
    id: "blog",
    label: "Blog",
    description: "Historias, tendencias y guías financieras.",
    path: "/blog",
    accent: "text-[#102a72]",
    links: [
      { label: "Inicio Blog", path: "/blog" },
      { label: "Educación financiera", path: "/blog/educacion-financiera" },
      { label: "Negocios", path: "/blog/negocios" },
      { label: "Bienestar y sostenibilidad", path: "/blog/bienestar" },
      { label: "Innovación", path: "/blog/innovacion" },
      { label: "Tendencias", path: "/blog/tendencias" },
      { label: "Actualidad", path: "/blog/actualidad" },
    ],
    cta: {
      label: "Iniciar sesión",
      path: "/personas/iniciar-sesion",
      color: "bg-blue-600 hover:bg-blue-700",
    },
  },
  {
    id: "sueldo",
    label: "Sueldo Inteligente",
    description: "Activa el abono automático de tus remesas.",
    path: "/sueldo-inteligente",
    accent: "text-[#0054c9]",
    links: [
      { label: "Beneficios", path: "/sueldo-inteligente#beneficios" },
      { label: "Características", path: "/sueldo-inteligente#caracteristicas" },
      { label: "Activación", path: "/sueldo-inteligente#activacion" },
      { label: "Tasas y tarifas", path: "/sueldo-inteligente#tasas" },
      { label: "Documentos", path: "/sueldo-inteligente#documentos" },
      { label: "Preguntas frecuentes", path: "/sueldo-inteligente#faq" },
    ],
    cta: {
      label: "Comenzar ahora",
      path: "/personas/iniciar-sesion",
      color: "bg-[#0054c9] hover:bg-[#003e94]",
    },
  },
];

const COMMON_LINKS = [
  { label: "¿Quiénes somos?", path: "/quienes-somos" },
  { label: "Transparencia", path: "/transparencia" },
  { label: "Consumidor", path: "/consumidor" },
  { label: "Política de datos", path: "/politica-tratamiento-datos" },
];

const resolveCategoryFromPath = (pathname) => {
  if (pathname.startsWith("/empresas")) return "empresas";
  if (pathname.startsWith("/valor360")) return "valor360";
  if (pathname.startsWith("/sueldo-inteligente")) return "sueldo";
  if (pathname.startsWith("/blog")) return "blog";
  return "personas";
};

const MobileNavDrawer = () => {
  const location = useLocation();
  const onEmpresas = location.pathname.startsWith("/empresas");
  const { t } = useEmpresasLanguageOptional();
  const currentCategory = useMemo(() => resolveCategoryFromPath(location.pathname), [location.pathname]);
  const isEmpresasLangSection = onEmpresas;
  const mobileChrome = onEmpresas ? t.chrome.mobile : null;

  const primarySections = useMemo(() => {
    if (!onEmpresas) return PRIMARY_SECTIONS;
    const emp = t.chrome.mobile.empresas;
    return PRIMARY_SECTIONS.map((section) => {
      if (section.id !== "empresas") return section;
      return {
        ...section,
        label: emp.label,
        description: emp.description,
        links: [
          { label: emp.inicio, path: "/empresas" },
          { label: emp.beneficios, path: "/empresas#beneficios" },
          { label: emp.cfoFraccional, path: "/empresas/direccion-de-innovacion-financiera" },
          { label: emp.gestionCaja, path: "/empresas/gestion-de-caja" },
          { label: emp.cez, path: "/empresas/consejo-empresarial-sumapaz" },
          { label: emp.gestionAportes, path: "/empresas/gestion-de-aportes" },
          { label: emp.sueldoInteligente, path: "/sueldo-inteligente" },
          { label: emp.blogEmpresarial, path: "/empresas/blog" },
          { label: emp.tramitesDigitales, path: "/empresas/tramites-digitales" },
        ],
        cta: {
          ...section.cta,
          label: emp.cta,
        },
      };
    });
  }, [onEmpresas, t]);

  const commonLinks = useMemo(() => {
    if (!onEmpresas) return COMMON_LINKS;
    const common = t.chrome.mobile.common;
    return [
      { label: common.quienesSomos, path: "/quienes-somos" },
      { label: common.transparencia, path: "/transparencia" },
      { label: common.consumidor, path: "/consumidor" },
      { label: common.politicaDatos, path: "/politica-tratamiento-datos" },
    ];
  }, [onEmpresas, t]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(currentCategory);
  const [openCategory, setOpenCategory] = useState(currentCategory);

  useEffect(() => {
    setActiveCategory(currentCategory);
    setOpenCategory(currentCategory);
  }, [currentCategory]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const closeDrawer = () => setIsOpen(false);
  const openDrawer = () => setIsOpen(true);

  const activeConfig =
    useMemo(() => primarySections.find((section) => section.id === (openCategory || activeCategory)), [
      activeCategory,
      openCategory,
      primarySections,
    ]) || primarySections[0];

  return (
    <>
      <nav className="fixed top-0 left-0 w-full bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50 lg:hidden">
        <div className="flex items-center justify-between h-16 px-4">
          <Link to="/" className="flex items-center">
            <img src={moulogo} alt="MEU Aliado" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            {isEmpresasLangSection && <CfoLanguageToggle variant="light" />}
            <Link
              to={onEmpresas ? "/empresas/iniciar-sesion" : "/personas/iniciar-sesion"}
              className="text-sm font-semibold text-[#1746a2]"
              onClick={closeDrawer}
            >
              {mobileChrome?.ingresar ?? "Ingresar"}
            </Link>
            <button
              type="button"
              onClick={openDrawer}
              className="p-2 rounded-full border border-slate-300 text-[#102a72] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1746a2]"
            >
              <span className="sr-only">{mobileChrome?.abrirMenu ?? "Abrir menú principal"}</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {isOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={closeDrawer} />
          <div className="relative ml-auto h-full w-full max-w-sm bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#7c8ba1]">
                  {mobileChrome?.explorar ?? "Explora"}
                </p>
                <p className="text-lg font-bold text-[#102a72]">{activeConfig?.label}</p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 rounded-full border border-slate-300 text-[#102a72] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#102a72]"
              >
                <span className="sr-only">{mobileChrome?.cerrarMenu ?? "Cerrar menú"}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#7c8ba1]">
                  {mobileChrome?.categoriasPrincipales ?? "Categorías principales"}
                </p>
                <div className="mt-3 flex flex-col gap-3">
                  {primarySections.map((section) => {
                    const isOpen = openCategory === section.id;
                    return (
                      <div
                        key={section.id}
                        className={`rounded-3xl border ${isOpen ? "border-[#1746a2] bg-[#e7efff]" : "border-slate-200 bg-white"} transition-colors duration-150`}
                      >
                        <button
                          type="button"
                          className="w-full px-4 py-3 text-left"
                          onClick={() => {
                            setActiveCategory(section.id);
                            setOpenCategory((prev) => (prev === section.id ? null : section.id));
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-[#102a72]">{section.label}</p>
                              <p className="text-xs text-[#7c8ba1] mt-1">{section.description}</p>
                            </div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 text-[#7c8ba1] transition-transform ${isOpen ? "rotate-90" : ""}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 space-y-2">
                            <Link
                              to={section.path}
                              onClick={closeDrawer}
                              className="flex items-center gap-2 text-xs font-semibold text-[#1746a2] hover:text-[#0f2f74]"
                            >
                              {mobileChrome?.irA ?? "Ir a"} {section.label}
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                            <div className="space-y-2">
                              {section.links.map((link) => (
                                <Link
                                  key={`${section.id}-${link.path}`}
                                  to={link.path}
                                  onClick={closeDrawer}
                                  className="flex items-center justify-between rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-[#102a72] shadow-sm hover:bg-slate-100"
                                >
                                  {link.label}
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#7c8ba1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                  </svg>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {activeConfig?.cta && (
                <Link
                  to={activeConfig.cta.path}
                  onClick={closeDrawer}
                  className={`block w-full text-center text-white font-semibold py-3 rounded-2xl ${activeConfig.cta.color}`}
                >
                  {activeConfig.cta.label}
                </Link>
              )}

              <div className="border-t border-slate-100 pt-5">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#7c8ba1]">
                  {mobileChrome?.masDeMeu ?? "Más de MEU"}
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {commonLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeDrawer}
                      className="px-4 py-2 rounded-full bg-slate-100 text-xs font-semibold text-[#102a72] hover:bg-slate-200"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNavDrawer;
